import React, { useEffect, useRef, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { api, formatErr } from "@/lib/api";
import CountdownTimer from "@/components/CountdownTimer";
import MapEmbed from "@/components/MapEmbed";
import { PALETTES } from "@/lib/eventOptions";
import { format } from "date-fns";
import { Button } from "@/components/ui/button";
import { Lock, ArrowLeft, Download } from "lucide-react";
import PublishDialog from "@/components/PublishDialog";
import Navbar from "@/components/Navbar";
import { themeMotifConfig, ThemeOrnament } from "@/lib/themeMotif";
import ThemeBand from "@/components/ThemeBand";
import ThemeBandImage from "@/components/ThemeBandImage";
import PrintableInviteCard from "@/components/PrintableInviteCard";
import { publicImageUrl } from "@/components/ImageUpload";
import { toast } from "sonner";

export default function PreviewInvite() {
  const { id } = useParams();
  const [event, setEvent] = useState(null);
  const [err, setErr] = useState(null);
  const [publishOpen, setPublishOpen] = useState(false);
  const [downloading, setDownloading] = useState(false);
  const inviteRef = useRef(null);
  const [themeArt, setThemeArt] = useState({ top: null, bottom: null });
  const printableRef = useRef(null);

  const load = async () => {
    try { const { data } = await api.get(`/events/${id}`); setEvent(data); }
    catch (e) { setErr(formatErr(e.response?.data?.detail)); }
  };
  useEffect(() => { load(); }, [id]);

  useEffect(() => {
    if (!event || event.motif_density !== "busy") return;
    const params = new URLSearchParams({
      theme: event.theme || "",
      custom_theme: event.custom_theme || "",
      event_type: event.event_type || "",
    }).toString();
    api.get(`/theme-art?${params}`).then(({ data }) => {
      if (data?.top_path && data?.bottom_path) {
        setThemeArt({
          top: `${window.location.origin}/api/uploads/file/${data.top_path}`,
          bottom: `${window.location.origin}/api/uploads/file/${data.bottom_path}`,
        });
      }
    }).catch(() => {});
  }, [event]);

  const downloadInvite = async () => {
    if (!event) return;
    setDownloading(true);
    try {
      const el = printableRef.current;
      if (!el) { toast.error("Preparing the invite — try again in a moment"); return; }
      const imgs = el.querySelectorAll("img");
      await Promise.all(Array.from(imgs).map((img) => {
        if (img.complete && img.naturalWidth > 0) return Promise.resolve();
        return new Promise((res) => { img.onload = res; img.onerror = res; });
      }));
      const [{ default: html2canvas }, jsPDFMod] = await Promise.all([
        import("html2canvas"),
        import("jspdf"),
      ]);
      const jsPDF = jsPDFMod.jsPDF || jsPDFMod.default;
      const canvas = await html2canvas(el, { backgroundColor: null, scale: 2, useCORS: true, logging: false });
      const pdf = new jsPDF({ unit: "mm", format: "a5", orientation: "portrait" });
      const pageW = pdf.internal.pageSize.getWidth();
      const pageH = pdf.internal.pageSize.getHeight();
      const imgData = canvas.toDataURL("image/jpeg", 0.92);
      const canvasRatio = canvas.width / canvas.height;
      const pageRatio = pageW / pageH;
      let rw, rh, ox = 0, oy = 0;
      if (canvasRatio > pageRatio) { rw = pageW; rh = pageW / canvasRatio; oy = (pageH - rh) / 2; }
      else { rh = pageH; rw = pageH * canvasRatio; ox = (pageW - rw) / 2; }
      pdf.addImage(imgData, "JPEG", ox, oy, rw, rh);
      pdf.save(`${event.slug || "invite"}.pdf`);
      toast.success("Invite PDF saved");
    } catch (e) {
      console.error("PDF render failed:", e);
      toast.error("Could not render PDF — try again");
    } finally {
      setDownloading(false);
    }
  };

  if (err) return <div className="min-h-screen bg-[#FBF9F4]"><Navbar /><div className="p-10 text-neutral-600">{err}</div></div>;
  if (!event) return <div className="min-h-screen flex items-center justify-center text-neutral-500">Loading…</div>;

  const palette = PALETTES.find((p) => p.name === event.palette) || PALETTES[0];
  const isPublished = event.status === "published";
  const motif = themeMotifConfig(event.theme, event.custom_theme);
  const bannerUrl = event.banner_path ? publicImageUrl(event.banner_path) : null;
  const bannerPos = event.banner_position || "top";
  const L = (v, d) => (v && String(v).trim()) ? v : d;
  const introText = L(event.intro_label, `A ${event.event_type.replace("_", " ")} to come`);
  const hostedByText = L(event.hosted_by_label, "Hosted by");
  const countdownText = L(event.countdown_label, "The moment approaches");
  const whenText = L(event.when_label, "When");
  const whereText = L(event.where_label, "Where");
  const attireText = L(event.attire_label, "Attire");
  const aboutText = L(event.about_label, "About");
  const hostMsgText = L(event.host_message_label, "A word from your host");
  const mapText = L(event.map_label, "Find your way");
  const rsvpIntroText = L(event.rsvp_intro_label, "Kindly respond");
  const rsvpTitleText = L(event.rsvp_title_label, "RSVP");
  let dateStr = ""; let timeStr = "";
  try { const d = new Date(event.event_datetime); dateStr = format(d, "EEEE, MMMM d, yyyy"); timeStr = format(d, "p"); } catch {}

  const bannerEl = bannerUrl ? (
    <div data-testid="preview-banner" className="w-full h-64 sm:h-80 overflow-hidden">
      <img src={bannerUrl} alt="" crossOrigin="anonymous" className="w-full h-full object-cover" />
    </div>
  ) : null;
  const topBandEl = event.motif_density === "busy" ? (
    <div className={`w-full ${bannerPos === "after_band" && bannerUrl ? "" : "-mb-2"}`}>
      <ThemeBandImage theme={event.theme} customTheme={event.custom_theme} eventType={event.event_type} variant="top" triggerGenerate={true} />
    </div>
  ) : null;

  return (
    <>
      {/* Preview ribbon (draft) */}
      {!isPublished && (
        <div data-testid="preview-ribbon" className="sticky top-0 z-50 bg-neutral-900 text-white">
          <div className="max-w-7xl mx-auto px-6 lg:px-10 py-3 flex items-center justify-between flex-wrap gap-3">
            <div className="flex items-center gap-3">
              <Lock size={14} />
              <span className="text-[10px] tracking-[0.3em] uppercase">Preview · Not published · Link not shareable</span>
            </div>
            <div className="flex items-center gap-2">
              <Link to={`/events/${id}`}>
                <Button variant="ghost" size="sm" data-testid="preview-back" className="rounded-sm text-white hover:bg-white/10">
                  <ArrowLeft size={14} className="mr-1" /> Back
                </Button>
              </Link>
              <Button
                size="sm"
                onClick={() => setPublishOpen(true)}
                data-testid="preview-publish-btn"
                className="rounded-sm bg-white text-neutral-900 hover:bg-neutral-100"
              >
                Publish this invite
              </Button>
            </div>
          </div>
        </div>
      )}

      {/* Published host toolbar */}
      {isPublished && (
        <div data-testid="preview-published-bar" className="sticky top-0 z-50 bg-white border-b border-black/10">
          <div className="max-w-7xl mx-auto px-6 lg:px-10 py-3 flex items-center justify-between flex-wrap gap-3">
            <div className="flex items-center gap-3">
              <span className="text-[10px] tracking-[0.3em] uppercase text-neutral-500">Host preview · Published</span>
            </div>
            <div className="flex items-center gap-2">
              <Link to={`/events/${id}`}>
                <Button variant="ghost" size="sm" data-testid="preview-back" className="rounded-sm">
                  <ArrowLeft size={14} className="mr-1" /> Back
                </Button>
              </Link>
              <Button size="sm" onClick={downloadInvite} disabled={downloading} data-testid="download-invite-btn" className="rounded-sm bg-neutral-900 hover:bg-neutral-800">
                <Download size={14} className="mr-2" /> {downloading ? "Rendering…" : "Download PDF"}
              </Button>
            </div>
          </div>
        </div>
      )}

      <div ref={inviteRef} className="min-h-screen" style={{ background: palette.bg, color: palette.primary, fontFamily: "'Manrope', sans-serif" }}>
        {bannerPos === "top" && bannerEl}
        {topBandEl}
        {bannerPos === "after_band" && bannerEl}
        <section className="relative">
          <div className={`relative max-w-5xl mx-auto px-6 lg:px-10 ${event.motif_density === "busy" ? "pt-4" : "pt-16 lg:pt-24"} pb-16 text-center`}>
            <div className="text-[10px] tracking-[0.35em] uppercase opacity-70">{introText}</div>
            {event.show_theme_motif !== false && (
              <div className="mt-6"><ThemeOrnament config={motif} color={palette.primary} /></div>
            )}
            <h1 style={{ fontFamily: "'Cormorant Garamond', serif" }} className="mt-8 text-5xl sm:text-7xl lg:text-[6.5rem] leading-[0.95] tracking-tight font-light">
              {event.title}
            </h1>
            {event.show_host_name !== false && event.host_name && (
              <div className="mt-10 text-sm tracking-[0.2em] uppercase opacity-80">{hostedByText} {event.host_name}</div>
            )}
          </div>
          <div className="max-w-4xl mx-auto px-6"><div className="h-px opacity-20" style={{ background: palette.primary }} /></div>
        </section>

        {event.show_countdown !== false && (
          <section className="py-16">
            <div className="text-center text-[10px] tracking-[0.35em] uppercase opacity-70 mb-8">{countdownText}</div>
            <CountdownTimer target={event.event_datetime} accent={palette.primary} />
          </section>
        )}

        <section className="max-w-5xl mx-auto px-6 lg:px-10 pb-16">
          <div className="grid grid-cols-12 gap-4">
            <div className="col-span-12 md:col-span-6 border p-8" style={{ borderColor: `${palette.primary}22` }}>
              <div className="text-[10px] tracking-[0.3em] uppercase opacity-70">{whenText}</div>
              <div style={{ fontFamily: "'Cormorant Garamond', serif" }} className="text-3xl sm:text-4xl mt-3 font-normal">{dateStr}</div>
              <div className="mt-2 text-lg opacity-80">{timeStr}</div>
            </div>
            <div className="col-span-12 md:col-span-6 border p-8" style={{ borderColor: `${palette.primary}22` }}>
              <div className="text-[10px] tracking-[0.3em] uppercase opacity-70">{whereText}</div>
              <div style={{ fontFamily: "'Cormorant Garamond', serif" }} className="text-3xl sm:text-4xl mt-3 font-normal">{event.location_name}</div>
              <div className="mt-2 text-base opacity-80">{event.location_address}</div>
            </div>
            {event.show_dress_code !== false && event.dress_code && (
              <div className="col-span-12 md:col-span-6 border p-8" style={{ borderColor: `${palette.primary}22` }}>
                <div className="text-[10px] tracking-[0.3em] uppercase opacity-70">{attireText}</div>
                <div style={{ fontFamily: "'Cormorant Garamond', serif" }} className="text-2xl mt-3">{event.dress_code}</div>
              </div>
            )}
            {event.show_description !== false && event.description && (
              <div className="col-span-12 md:col-span-6 border p-8" style={{ borderColor: `${palette.primary}22` }}>
                <div className="text-[10px] tracking-[0.3em] uppercase opacity-70">{aboutText}</div>
                <p className="mt-3 text-base leading-relaxed opacity-90 whitespace-pre-wrap">{event.description}</p>
              </div>
            )}
          </div>
        </section>

        {Array.isArray(event.custom_sections) && event.custom_sections.filter(s => (s?.title || "").trim() || (s?.body || "").trim()).length > 0 && (
          <section className="max-w-5xl mx-auto px-6 lg:px-10 pb-16">
            <div className="grid grid-cols-12 gap-4">
              {event.custom_sections
                .filter(s => (s?.title || "").trim() || (s?.body || "").trim())
                .map((s, i) => (
                  <div key={i} className="col-span-12 md:col-span-6 border p-8" style={{ borderColor: `${palette.primary}22` }}>
                    {(s.title || "").trim() && (
                      <div className="text-[10px] tracking-[0.3em] uppercase opacity-70">{s.title}</div>
                    )}
                    {(s.body || "").trim() && (
                      <p className="mt-3 text-base leading-relaxed opacity-90 whitespace-pre-wrap">{s.body}</p>
                    )}
                  </div>
                ))}
            </div>
          </section>
        )}

        {event.show_host_message !== false && event.host_message && (
          <section className="max-w-3xl mx-auto px-6 lg:px-10 pb-16 text-center">
            <div className="text-[10px] tracking-[0.35em] uppercase opacity-70 mb-4">{hostMsgText}</div>
            <p style={{ fontFamily: "'Cormorant Garamond', serif" }} className="text-2xl sm:text-3xl italic font-light leading-relaxed">
              &ldquo;{event.host_message}&rdquo;
            </p>
          </section>
        )}

        <section className="max-w-5xl mx-auto px-6 lg:px-10 pb-16">
          <div className="text-[10px] tracking-[0.35em] uppercase opacity-70 mb-4 text-center">{mapText}</div>
          <MapEmbed address={event.location_address} name={event.location_name} />
        </section>

        {/* Locked RSVP preview (draft only) */}
        {!isPublished && (
          <section className="max-w-2xl mx-auto px-6 lg:px-10 pb-24">
            <div className="text-center mb-8">
              <div className="text-[10px] tracking-[0.35em] uppercase opacity-70">{rsvpIntroText}</div>
              <h2 style={{ fontFamily: "'Cormorant Garamond', serif" }} className="text-4xl sm:text-5xl mt-3 font-light tracking-tight">{rsvpTitleText}</h2>
            </div>
            <div
              className="relative border p-10 bg-white/60 text-center"
              style={{ borderColor: `${palette.primary}22` }}
              data-testid="preview-rsvp-locked"
            >
              <div className="inline-flex items-center gap-2 text-[10px] tracking-[0.3em] uppercase opacity-70 border border-current px-3 py-2">
                <Lock size={12} /> Locked in preview
              </div>
              <p className="mt-5 text-neutral-700">Publish this invite to activate the RSVP form for your guests.</p>
              <Button onClick={() => setPublishOpen(true)} data-testid="preview-rsvp-publish" className="mt-5 rounded-sm" style={{ background: palette.primary, color: "white" }}>
                Publish to unlock
              </Button>
            </div>
          </section>
        )}

        {event.motif_density === "busy" && (
          <div className="w-full mt-4">
            <ThemeBandImage theme={event.theme} customTheme={event.custom_theme} eventType={event.event_type} variant="bottom" triggerGenerate={true} />
          </div>
        )}

        <footer className="py-8 text-center text-[10px] tracking-[0.3em] uppercase opacity-60">
          {isPublished ? "Invitation" : "Preview"} by <a href="/" className="underline underline-offset-4">Soirée</a>
        </footer>
      </div>

      <PublishDialog open={publishOpen} onOpenChange={setPublishOpen} event={event} onPublished={load} />

      <div style={{ position: "fixed", left: -10000, top: 0, pointerEvents: "none" }} aria-hidden="true">
        <PrintableInviteCard
          ref={printableRef}
          event={{ ...event, theme_art_top_url: themeArt.top, theme_art_bottom_url: themeArt.bottom }}
          publicUrl={`${window.location.origin}/i/${event.slug}`}
        />
      </div>
    </>
  );
}
